import React, { useState } from "react";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  Divider,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { brand } from "../theme";
import { ROLES } from "../constants/roles";

import DashboardIcon from "@mui/icons-material/Dashboard";
import SettingsIcon from "@mui/icons-material/Settings";
import PersonIcon from "@mui/icons-material/Person";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import GroupIcon from "@mui/icons-material/Group";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import MenuIcon from "@mui/icons-material/Menu";
import { QuestionMark } from "@mui/icons-material";
import LogoutIcon from "@mui/icons-material/Logout";
import ProductsIcon from "@mui/icons-material/Inventory2";
import HistoryIcon from "@mui/icons-material/History";
import GavelIcon from "@mui/icons-material/Gavel";

const SIDEBAR_BG = brand.black;
const SIDEBAR_HEADER_BG = "#141414";
const ACTIVE_BG = "rgba(226,85,60,0.18)";
const ACTIVE_COLOR = brand.terracotta;
const INACTIVE_COLOR = brand.gray;
const HOVER_BG = "rgba(255,255,255,0.06)";
const MUTED_TEXT = "rgba(255,255,255,0.55)";

const Sidebar = ({ open, onClose, isDesktop }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const toggleCollapse = () => setCollapsed((prev) => !prev);

  const sharedMenus = [
    { label: "Dashboard", path: "/dashboard", icon: <DashboardIcon fontSize="small" /> },
    { label: "Agencies", path: "/agencies", icon: <GroupIcon fontSize="small" /> },
    { label: "Notifications", path: "/notifications", icon: <SettingsIcon fontSize="small" /> },
    { label: "Audit Log", path: "/audit-log", icon: <HistoryIcon fontSize="small" /> },
    { label: "Profile", path: "/profile", icon: <PersonIcon fontSize="small" /> },
  ];

  const roleMenus = {
    super_admin: [
      { label: "Dashboard", path: "/dashboard", icon: <DashboardIcon fontSize="small" /> },
      { label: "Agencies", path: "/agencies", icon: <GroupIcon fontSize="small" /> },
      { label: "Manage Admins", path: "/manage-admins", icon: <PersonIcon fontSize="small" /> },
      { label: "Audit Log", path: "/audit-log", icon: <HistoryIcon fontSize="small" /> },
      { label: "System Settings", path: "/settings", icon: <SettingsIcon fontSize="small" /> },
    ],
    agency_owner: [
      { label: "Dashboard", path: "/dashboard", icon: <DashboardIcon fontSize="small" /> },
      { label: "Request PCC", path: "/pcc-request", icon: <BusinessCenterIcon fontSize="small" /> },
      { label: "Explore Products", path: "/products", icon: <BookOnlineIcon fontSize="small" /> },
      { label: "Contract", path: "/contract", icon: <GavelIcon fontSize="small" /> },
      { label: "Profile", path: "/profile", icon: <PersonIcon fontSize="small" /> },
      { label: "FAQs", path: "/faq", icon: <QuestionMark fontSize="small" /> },
    ],
    agency_staff: [
      { label: "Dashboard", path: "/dashboard", icon: <DashboardIcon fontSize="small" /> },
      { label: "Products", path: "/products", icon: <BookOnlineIcon fontSize="small" /> },
    ],
    [ROLES.SALES_ACCOUNT_SPECIALIST]: sharedMenus,
    [ROLES.HELPDESK]: sharedMenus,
    [ROLES.TECHNICAL_SUPPORT_SPECIALIST]: [
      ...sharedMenus,
      { label: "Manage Products", path: "/create-products", icon: <ProductsIcon fontSize="small" /> },
    ],
  };

  const menus = roleMenus[user?.role] || [];

  const roleLabel = user?.role?.replace(/_/g, " ") ?? "";
  const userInitial =
    (user?.name?.charAt(0) || user?.email?.charAt(0) || "?").toUpperCase();

  const SidebarContent = (
    <Box
      sx={{
        width: collapsed ? 72 : 240,
        transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)",
        bgcolor: SIDEBAR_BG,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          px: collapsed ? 1 : 2,
          py: 1.75,
          bgcolor: SIDEBAR_HEADER_BG,
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          minHeight: 64,
        }}
      >
        {!collapsed && (
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: MUTED_TEXT,
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                fontSize: "0.65rem",
                display: "block",
                mb: 0.25,
              }}
            >
              {roleLabel}
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{ color: brand.white, fontWeight: 700, fontSize: "0.95rem" }}
            >
              Client Portal
            </Typography>
          </Box>
        )}
        <IconButton
          size="small"
          onClick={toggleCollapse}
          sx={{ color: MUTED_TEXT, "&:hover": { color: brand.white, bgcolor: HOVER_BG } }}
        >
          {collapsed ? <MenuIcon fontSize="small" /> : <MenuOpenIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Nav Items */}
      <List sx={{ flexGrow: 1, px: 1, pt: 1.5, pb: 1 }}>
        {menus.map((menu, i) => {
          const isActive = location.pathname === menu.path;

          const listItem = (
            <ListItemButton
              key={i}
              onClick={() => {
                navigate(menu.path);
                if (!isDesktop) onClose();
              }}
              sx={{
                borderRadius: "8px",
                mb: 0.5,
                px: collapsed ? 0 : 1.5,
                py: 1,
                justifyContent: collapsed ? "center" : "flex-start",
                backgroundColor: isActive ? ACTIVE_BG : "transparent",
                color: isActive ? ACTIVE_COLOR : INACTIVE_COLOR,
                position: "relative",
                "&:hover": {
                  backgroundColor: isActive ? ACTIVE_BG : HOVER_BG,
                  color: isActive ? ACTIVE_COLOR : brand.white,
                },
                ...(isActive && {
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: "20%",
                    height: "60%",
                    width: 3,
                    borderRadius: "0 3px 3px 0",
                    backgroundColor: ACTIVE_COLOR,
                  },
                }),
              }}
            >
              <ListItemIcon
                sx={{
                  color: "inherit",
                  minWidth: collapsed ? "unset" : 36,
                  "& svg": { fontSize: "1.15rem" },
                }}
              >
                {menu.icon}
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={menu.label}
                  primaryTypographyProps={{
                    fontSize: "0.875rem",
                    fontWeight: isActive ? 600 : 500,
                  }}
                />
              )}
            </ListItemButton>
          );

          return collapsed ? (
            <Tooltip title={menu.label} placement="right" key={i}>
              {listItem}
            </Tooltip>
          ) : (
            listItem
          );
        })}
      </List>

      {/* User + Logout */}
      <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.06)", px: 1, py: 1.5 }}>
        {!collapsed && (
          <Box sx={{ px: 1.5, pb: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: "0.8rem",
                bgcolor: brand.terracotta,
                flexShrink: 0,
              }}
            >
              {userInitial}
            </Avatar>
            <Box sx={{ overflow: "hidden" }}>
              <Typography
                sx={{
                  color: brand.white,
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user?.name || user?.email}
              </Typography>
              <Typography
                sx={{
                  color: MUTED_TEXT,
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  textTransform: "capitalize",
                }}
              >
                {roleLabel}
              </Typography>
            </Box>
          </Box>
        )}
        {collapsed ? (
          <Tooltip title="Logout" placement="right">
            <ListItemButton
              onClick={logout}
              sx={{
                borderRadius: "8px",
                justifyContent: "center",
                color: MUTED_TEXT,
                "&:hover": { bgcolor: HOVER_BG, color: "#f87171" },
              }}
            >
              <ListItemIcon sx={{ minWidth: "unset", color: "inherit" }}>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        ) : (
          <ListItemButton
            onClick={logout}
            sx={{
              borderRadius: "8px",
              color: MUTED_TEXT,
              px: 1.5,
              py: 0.875,
              "&:hover": { bgcolor: "rgba(248,113,113,0.1)", color: "#f87171" },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }}
            />
          </ListItemButton>
        )}
      </Box>
    </Box>
  );

  if (isDesktop) {
    return (
      <Box
        sx={{
          width: collapsed ? 72 : 240,
          flexShrink: 0,
          display: { xs: "none", lg: "block" },
          transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        {SidebarContent}
      </Box>
    );
  }

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      PaperProps={{ sx: { bgcolor: SIDEBAR_BG, border: "none" } }}
    >
      {SidebarContent}
    </Drawer>
  );
};

export default Sidebar;
